import * as React from "react";
import clsx from "clsx";
import { useParams } from "react-router";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { CurrentSnapshotCitationProvider } from "@/modules/snapshots/contexts/CurrentSnapshotCitationContext";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { useGetBusinessReportOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { isBusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { BusinessReportOutcomeContent } from "./businessReportOutcomeContent";
import { SidePanel } from "./components/SidePanel";
import { SnapshotCitationDrawer } from "./components/SnapshotCitationDrawer";
import { ReportPanelType } from "./types";
import type { SidePanelTab } from "@/components/chat/types";

const COLLECTION_ENABLED_TABS: ReadonlyArray<SidePanelTab> = ["improve", "library"];
const SINGLE_SNAPSHOT_ENABLED_TABS: ReadonlyArray<SidePanelTab> = ["pdf", "improve", "library"];

const BusinessReportOutcomeDetailsLayout = () => {
  const { outcomeId } = useParams<{ outcomeId: string }>();
  const { data: outcome } = useGetBusinessReportOutcomeQuery(outcomeId ?? "", { skip: !outcomeId });
  const isBusinessReport = isBusinessReportOutcome(outcome);
  const enabledTabs = isBusinessReport ? COLLECTION_ENABLED_TABS : SINGLE_SNAPSHOT_ENABLED_TABS;

  const [activePanel, setActivePanel] = React.useState<ReportPanelType>("pdf");
  const { currentCitation } = useCurrentPdf();

  React.useEffect(() => {
    if (isBusinessReport) {
      setActivePanel(prev => (prev === "pdf" ? null : prev));
    }
  }, [isBusinessReport]);

  React.useEffect(() => {
    if (currentCitation && !isBusinessReport) {
      setActivePanel("pdf");
    }
  }, [currentCitation, isBusinessReport]);

  const handlePanelToggle = React.useCallback((panel: Exclude<ReportPanelType, null>) => {
    setActivePanel(prev => (prev === panel ? null : panel));
  }, []);

  const handlePanelClose = React.useCallback(() => {
    setActivePanel(null);
  }, []);

  const isPanelOpen = activePanel !== null;

  return (
    <div className="absolute inset-0 flex overflow-hidden bg-[#f7f3ee]">
      <div
        className={clsx("flex flex-col transition-transform duration-500 ease-in-out", {
          "min-w-0 flex-1 translate-x-0": isBusinessReport || isPanelOpen,
          "w-1/2 translate-x-1/2": !isBusinessReport && !isPanelOpen,
        })}
      >
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col px-8 pt-7">
          <BusinessReportOutcomeContent
            outcomeId={outcomeId || ""}
            isPanelOpen={isPanelOpen}
            onPanelToggle={handlePanelToggle}
          />
        </div>
      </div>

      {!isBusinessReport && (
        <SidePanel
          activePanel={activePanel}
          isPanelOpen={isPanelOpen}
          onPanelChange={setActivePanel}
          onClose={handlePanelClose}
          enabledTabs={enabledTabs}
        />
      )}

      {isBusinessReport && <SnapshotCitationDrawer />}
    </div>
  );
};

export const BusinessReportOutcomeDetailsView = () => (
  <CurrentPdfProvider>
    <CurrentSnapshotCitationProvider>
      <PromptGeneratorContextProvider>
        <BusinessReportOutcomeDetailsLayout />
      </PromptGeneratorContextProvider>
    </CurrentSnapshotCitationProvider>
  </CurrentPdfProvider>
);
